import type { MessageRecordType } from "@/api/business/types/message"
import { MessageTypeConstants } from "@/api/business/types/message"

export const RESERVATION_EXPIRED_NOTIFICATION_DEDUP_STORAGE_KEY
  = "tea-shop-2b-fe:reservation-expired-notification-keys:v1"
export const RESERVATION_EXPIRED_NOTIFICATION_DEDUP_MAX_KEYS = 200

function normalizeKeyPart(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value)
  }
  if (typeof value === "string" && value.trim()) {
    return value.trim()
  }
  return undefined
}

function buildReservationExpiredNotificationKey(message: MessageRecordType): string | undefined {
  try {
    const content = JSON.parse(message.messageContent) as Record<string, unknown>
    const reserveId = normalizeKeyPart(content.reserveId)
    const overtime = normalizeKeyPart(content.overtime)
    if (reserveId === undefined || overtime === undefined) {
      return undefined
    }
    return `reservation-expired:${message.shopId}:${reserveId}:${overtime}`
  }
  catch {
    return undefined
  }
}

function readStoredKeys(storage: Storage): string[] {
  const storedValue = storage.getItem(RESERVATION_EXPIRED_NOTIFICATION_DEDUP_STORAGE_KEY)
  if (!storedValue) {
    return []
  }
  try {
    const parsed = JSON.parse(storedValue)
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : []
  }
  catch {
    return []
  }
}

export function shouldDisplayReservationExpiredNotification(
  message: MessageRecordType,
  storage: Storage = window.sessionStorage,
): boolean {
  if (message.messageType !== MessageTypeConstants.MESSAGE_TYPE_RESERVATION_EXPIRED) {
    return true
  }

  const notificationKey = buildReservationExpiredNotificationKey(message)
  if (!notificationKey) {
    return true
  }

  try {
    const storedKeys = readStoredKeys(storage)
    if (storedKeys.includes(notificationKey)) {
      return false
    }

    const nextKeys = [...storedKeys, notificationKey]
      .slice(-RESERVATION_EXPIRED_NOTIFICATION_DEDUP_MAX_KEYS)
    storage.setItem(
      RESERVATION_EXPIRED_NOTIFICATION_DEDUP_STORAGE_KEY,
      JSON.stringify(nextKeys),
    )
    return true
  }
  catch {
    return true
  }
}
